import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLoadScript, Autocomplete } from '@react-google-maps/api';
import { fetchClientServices, getServiceTypes, getServiceLanguages } from '../slice/clientServicesSlice';
import ClientHeader from '../../client-header/components';
import ClientFooter from "../../client-footer/components";
import "../style/clientServices-style.css";
import ClientMapComponent from "../../client-map/components/index";
import { useParams, useLocation, useSearchParams } from "react-router-dom";

const libraries = ['places'];

const ClientServices = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const locationState = location.state;
  const { loading, error, clientServices, pagination, serviceTypes, serviceLanguage, additionalTags } = useSelector((state) => state.clientServices);
  const [page, setPage] = useState(1);
  const languageSelectRef = useRef(null);
  const tagSelectRef = useRef(null);
  const { serviceTypeId } = useParams();
  const [filters, setFilters] = useState({
    service_type: "",
    from_age: "",
    to_age: "",
    available_day: "",
    lat: "",
    long: "",
    address: "",
    city: "",
    state: "",
    country: "",
    service_mode: []
  });

  useEffect(() => {
  if (serviceTypeId) {
    setFilters((prev) => ({
      ...prev,
      service_type: serviceTypeId,
    }));
  }
}, [serviceTypeId]);

const [searchParams] = useSearchParams();

useEffect(() => {
  const locationDetails = {
    address: searchParams.get("address") || "",
    city: searchParams.get("city") || "",
    state: searchParams.get("state") || "",
    country: searchParams.get("country") || "",
    lat: searchParams.get("lat") || "",
    long: searchParams.get("long") || "",
  };

  // Apply to filters
  setFilters((prev) => ({
    ...prev,
    ...locationDetails,
  }));
}, [searchParams]);



const clearFilters = () => {
  // Clear Select2 dropdowns
  if (window.$ && window.$.fn.select2) {
    window.$(languageSelectRef.current).val(null).trigger("change");
    window.$(tagSelectRef.current).val(null).trigger("change");
  }

  // Reset filter state
  setFilters({
    service_type: "",
    from_age: "",
    to_age: "",
    available_day: "",
    lat: "",
    long: "",
    address: "",
    city: "",
    state: "",
    country: "",
    service_mode: [],
    language_support: [],
    additional_tags: [],
  });

  setPage(1);
};


useEffect(() => {
    // Wait for the DOM and jQuery to be ready
    if (window.$ && window.$.fn.select2) {
      window.$(languageSelectRef.current).select2({
        placeholder: "Select Language",
        width: "100%",
      });

      window.$(tagSelectRef.current).select2({
        placeholder: "Select Tag",
        width: "100%",
      });

      // Optional: Trigger change manually to sync React state
      window.$(languageSelectRef.current).on('change', (e) => {
        const selected = Array.from(e.target.selectedOptions).map(opt => opt.value);
        handleChange({ target: { name: 'language_support', value: selected, multiple: true, options: e.target.options } });
      });

      window.$(tagSelectRef.current).on('change', (e) => {
        const selected = Array.from(e.target.selectedOptions).map(opt => opt.value);
        handleChange({ target: { name: 'additional_tags', value: selected, multiple: true, options: e.target.options } });
      });
    }
  }, []);

  const autocompleteRef = useRef(null);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const handlePlaceChanged = () => {
  if (autocompleteRef.current) {
    const place = autocompleteRef.current.getPlace();
    // console.log("place", place);
    if (place && place.formatted_address) {
      const locationDetails = parsePlaceDetails(place);

      setFilters((prev) => ({
        ...prev,
        ...locationDetails,
        keyword: '', // reset keyword on address change
      }));
      setPage(1);
    }
  }
};

  const parsePlaceDetails = (place) => {
  const result = {
    address: place.formatted_address,
    city: "",
    state: "",
    country: "",
    lat: place.geometry?.location?.lat(),
    long: place.geometry?.location?.lng(),
  };

  for (const comp of place.address_components || []) {
    if (comp.types.includes("locality")) result.city = comp.long_name;
    else if (comp.types.includes("administrative_area_level_1")) result.state = comp.long_name;
    else if (comp.types.includes("country")) result.country = comp.long_name;
  }

  return result;
};


const filtersToFormData = (filters) => {
  const formData = new FormData();
  formData.forEach((value, key) => {
  console.log(`${key}:`, value);
});


  for (const key in filters) {
    const value = filters[key];

    if (key === "language" && Array.isArray(value)) {
      formData.append(key, value.join(",")); // Convert array to comma-separated
    } else if (value !== undefined && value !== null) {
      formData.append(key, value);
    }
  }

  return formData;
};

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPage(1);
  };

const handleChange = (e) => {
  const { name, value, options, multiple } = e.target;

  if (multiple) {
    const selectedValues = Array.from(options)
      .filter((option) => option.selected)
      .map((option) => option.value);

    setFilters((prev) => ({
      ...prev,
      [name]: selectedValues,
    }));
  } else if (name === 'ageRange') {
    const [from_age, to_age] = value.split('-');
    setFilters((prev) => ({
      ...prev,
      from_age,
      to_age,
    }));
  } else {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  setPage(1);
};

const handleServiceModeChange = (e) => {
    const { value, checked } = e.target;

    // Toggle the service mode in the state (add or remove)
    setFilters((prev) => {
      const newServiceModes = checked
        ? [...prev.service_mode, value]  // Add value if checked
        : prev.service_mode.filter((mode) => mode !== value);  // Remove value if unchecked

      return {
        ...prev,
        service_mode: newServiceModes,
      };
    });
  };

const prepareFilters = (filters) => {
  const cleaned = {};

  // ✅ Use lat and long consistently (not lng)
  if (filters.lat && filters.long) {
    cleaned.lat = filters.lat;
    cleaned.long = filters.long;
  } else {
    if (filters.address) cleaned.address = filters.address;
    if (filters.city) cleaned.city = filters.city;
    if (filters.state) cleaned.state = filters.state;
    if (filters.country) cleaned.country = filters.country;
  }

  // ✅ Add other non-empty filters
  for (const key in filters) {
    if (
      !['lat', 'long', 'address', 'city', 'state', 'country'].includes(key) &&
      filters[key] !== undefined &&
      filters[key] !== null &&
      filters[key] !== ''
    ) {
      cleaned[key] = filters[key];
    }
  }

  return cleaned;
};

useEffect(() => {
  const cleanedFilters = prepareFilters(filters);
  dispatch(fetchClientServices({ page, limit: 10, filters: cleanedFilters }));
  dispatch(getServiceLanguages());
  dispatch(getServiceTypes());
}, [dispatch, page, filters]);

  const formatTo12Hour = (timeStr) => {
    const [hours, minutes] = timeStr.split(':');
    const date = new Date();
    date.setHours(+hours, +minutes);
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    }).format(date);
  };


  console.log("clientServices", clientServices);
  console.log("serviceTypes", serviceTypes);
  const uniqueProvidersMap = new Map();

  clientServices.forEach(service => {
    const provider = service.provider;

    if (provider?.lat && provider?.long) {
      // Check if provider already exists in the map
      if (!uniqueProvidersMap.has(provider.id)) {
        uniqueProvidersMap.set(provider.id, {
          id: provider.id,
          lat: parseFloat(provider.lat),
          lng: parseFloat(provider.long),
          name: provider.company_name,
          address: provider.address,
          city: provider.city,
          state: provider.state,
          services: [], // Add a services array
        });
      }

      // Add the service info to the provider's services list
      const providerEntry = uniqueProvidersMap.get(provider.id);
      providerEntry.services.push({
        id: service.id,
        name: service.service_name,
      });
    }
  });

  const markers = Array.from(uniqueProvidersMap.values());

  const center = markers.length ? markers[0] : { lat: 37.7749, lng: -122.4194 };

  const getServiceTypeName = (typeId) => {
    const matchedType = serviceTypes.find(type => type.id === Number(typeId));
    return matchedType ? matchedType.issue_type : 'Unknown Type';
  };


  return (
    <>
      <ClientHeader />
      <main className="overflow-hidden">
        <section className="homepad pb-5">
          <div className="container">
            <div className="form-section bg-light rounded p-3 p-sm-4">
              <h4 className="text-center mb-4 pb-1 font-weight-bold text-primary font-size-40 line-hight-2 montserrat">Provider Services</h4>
              <form className="row g-3 g-xl-4">
              {/* <!-- Location + Buttons --> */}
              <div className="row align-items-center mb-3 g-3 g-xl-4">
                <div className="col-md col-lg-4">
                  {isLoaded && (
                    <Autocomplete
                      onLoad={(autocomplete) => (autocompleteRef.current = autocomplete)}
                      onPlaceChanged={handlePlaceChanged}
                    >
                    <input
                      type="text"
                      className="form-control rounded-pill px-3"
                      placeholder="Gujarat, Ahmedabad"
                      value={filters.address}
                      onChange={(e) =>
                        handleFilterChange({ ...filters, address: e.target.value, keyword: '' })
                      }
                    />
                    </Autocomplete>
                  )}
                </div>
                <div className="col-md col-lg-3">
                  <div className="col-md-auto text-md-start">
                  <div className="">

                      <div className="col-md-12 language-col">
                      <select
                        name="language_support"
                        className="form-select rounded-pill"
                        onChange={handleChange}
                        multiple
                        ref={languageSelectRef}
                      >
                        <option value="">Select Language</option>
                        {serviceLanguage
                          .filter(lang => !!lang)
                          .map((lang, index) => (
                            <option key={index} value={lang}>{lang}</option>
                          ))}
                      </select>
                  </div>
                <style>{`
        .select2-selection--multiple {
          padding: 0.675rem 2.25rem 0.675rem 1.3rem !important;
          border-radius: 50rem !important;
          border: 1px solid #ced4da;
          min-height: calc(1.5em + 0.75rem + 2px);
        }

        .select2-selection__choice {
          background-color: #0d6efd;
          border: none;
          border-radius: 50rem !important;
          padding: 0.25rem 0.5rem;
        }

        /* Optional: Adjust placeholder appearance */
        .select2-search__field::placeholder {
          color: #6c757d;
        }
      `}</style>
                      
                  </div>
                </div>
                </div>
                <div className="col-md col-lg-3">
                   <div className="col-md-12 additional-col">
                    <select
                      name="additional_tags"
                      className="form-select rounded-pill"
                      onChange={handleChange}
                      multiple
                      ref={tagSelectRef}
                    >
                      <option value="">Select Tag</option>
                      {additionalTags
                        .filter(tag => !!tag)
                        .map((tag, index) => (
                          <option key={index} value={tag}>{tag}</option>
                        ))}
                    </select>
                  </div>
                </div>
                <div className="col-md col-lg-2">
                    <div>
                      <button type="button" onClick={clearFilters} className="btn btn-danger rounded-pill w-100">
                        Clear 
                      </button>
                    </div>
                </div>
              </div>

              {/* <!-- Filters --> */}
              
                <div className="col-md-12 col-lg-3 subcategories">
                  {/* <select
                    name="service_type"
                    className="form-select rounded-pill"
                    onChange={handleChange}
                  >
                    <option value="">Select Service Type</option>
                    {serviceTypes.map((type) => (
                      <option key={type.id} value={type.id}>{type.issue_type}</option>
                    ))}
                  </select> */}
                  <select
  name="service_type"
  className="form-select rounded-pill"
  onChange={handleChange}
  value={filters.service_type} // bind value
>
  <option value="">Select Service Type</option>
  {serviceTypes.map((type) => (
    <option key={type.id} value={type.id}>{type.issue_type}</option>
  ))}
</select>

                </div>

                <div className="col-md-12 col-lg-3 subcategories-col">
                  <select
                    name="ageRange"
                    className="form-select rounded-pill"
                    onChange={handleChange}
                  >
                    <option value="">Ages</option>
                    <option value="1-5">1-5</option>
                    <option value="5-10">5-10</option>
                    <option value="11-16">11-16</option>
                  </select>
                </div>

                <div className="col-md-12 col-lg-3 subcategories-col">
                  <select
                    name="available_day"
                    className="form-select rounded-pill"
                    onChange={handleChange}
                  >
                    <option value="">Select Duration</option>
                    <option value="1">1 Day</option>
                    <option value="3">3 Days</option>
                    <option value="5">5 Days</option>
                    <option value="7">1 Week</option>
                    <option value="10">10 Days</option>
                    <option value="15">15 Days</option>
                    <option value="20">20 Days</option>
                    <option value="30">30 Days</option>
                  </select>
                </div>

                                   <div className="col-md-12 col-lg-3 subcategories">
                        <label className='form-label text-primary mb-1 font-weight-semibold'>Activity Mode</label>
                        <div className="d-flex align-items-center gap-3">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              value="offline"
                              id="offline"
                              onChange={handleServiceModeChange}
                              checked={filters.service_mode && filters.service_mode.includes("offline")}
                            />
                            <label className="form-check-label" htmlFor="offline">
                              Offline
                            </label>
                          </div>
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              value="online"
                              id="online"
                              onChange={handleServiceModeChange}
                              checked={filters.service_mode && filters.service_mode.includes("online")}
                            />
                            <label className="form-check-label" htmlFor="online">
                              Online
                            </label>
                          </div>
                        </div>
                      </div>

                  
              </form>
            </div>
          </div>
        </section>

        <section className="homepad pt-0" style={{ minHeight: "60vh" }}>
          <div className="container">
            <div className="row d-flex align-items-stretch">
              <div className="col-lg-8 services-listpage" style={{ minHeight: "1312px" }}>
                <div className="pe-xl-3">
          {clientServices.length === 0 ? (
  <div className="text-center py-5">
    <h5 className="text-muted">No services available</h5>
  </div>
) : (
  clientServices.map((service) => {
                    const providerImageBaseUrl = process.env.REACT_APP_PROVIDER_IMAGE_BACKEND_URL;
                    const firstImage = service.provider?.images?.[0]?.image_name ?? 'default.png';
                    const backgroundImageUrl = `${providerImageBaseUrl}/${firstImage}`;

                    return (
                      <div
                        className="row"
                        key={service.id}
                        onClick={() => window.open(`/client-service/details/${service.id}`, '_blank')}
                        style={{
                          cursor: "pointer"
                        }}
                      >
                        <div className="col-12">
                          <div className="card activity-card d-md-flex flex-md-row p-4">
                            <div
                              className="position-relative services-list-box"
                              style={{
                                background: `url(${backgroundImageUrl}) no-repeat`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                width: '25%',
                                // height: '50%'
                              }}
                            >
                              {/* <span className="badge badge-custom position-absolute bottom-0 start-0 m-2 font-weight-medium font-size-14 text-primary px-3 py-2">
                                {Array.isArray(service.language_support) && service.language_support.length > 0 ? 'Bilingual' : ''}
                              </span> */}
                              <a href="#" className="text-decoration-none position-absolute top-0 end-0 m-2">
                                <i className="fa fa-heart-o text-white font-size-22" aria-hidden="true"></i>
                              </a>
                            </div>
                            <div className="card-body py-0 pe-0 ps-0 ps-md-4 ms-xl-1 mt-4 mt-md-0">
                              <h6 className="font-weight-bold text-primary font-size-22 mb-3 line-hight-2 montserrat">
                                {service.service_name}
                              </h6>
                              <div className="text-muted font-size-14 mb-2">
                                <strong>Type: </strong>{getServiceTypeName(service.service_type)}
                              </div>
                              <div className="text-danger-dark font-size-16 font-weight-medium mb-3">
                                by {service.provider?.company_name || 'Unknown Provider'}
                              </div>
                              <p dangerouslySetInnerHTML={{ __html: service.service_description }} />
                              <div className="d-flex justify-content-between align-items-center mt-4">
                                <div className="d-flex align-items-center flex-wrap">
                                  <span className="price me-3 text-danger-dark font-size-26 font-weight-medium">
                                    ${service.amount_per_session || 'N/A'}
                                  </span>
                                  { service.service_mode != '' ? (
                                    <span className="mt-1 rounded bg-light text-dark font-weight-normal py-2 px-3 me-2">
                                      
                                      <img
                                        src="/assets/images/bxsdydbjmcutirye.svg"
                                        className="img-fluid me-2"
                                        alt="Activity"
                                      />

                                      {Array.isArray(service.service_mode)
                                        ? service.service_mode.join(', ')
                                        : service.service_mode}
                                      
                                    </span>
                                  ) : ( '' )
                                  }
                                  { service.from_age != null ? (

                                     <span className="mt-1 rounded bg-light text-dark font-weight-normal py-2 px-3">

                                       Ages {service.from_age} - {service.to_age} years

                                     </span>

                                   ) : ('')

                                 }
                                </div>
                                <span className="rating-badge bg-primary font-size-16 rounded-pill text-white" onClick={() => window.open(`/client-service/details/${service.id}`, '_blank')}>
                                  <img
                                    src="/assets/images/eye.png"
                                    className="img-fluid me-1"
                                    alt="Activity"
                                    width="20"
                                  />
                                  View Details
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                  )}


                  <nav>
  <ul className="pagination mt-2 mb-0 justify-content-center">
    {/* Previous button */}
    <li className={`page-item ${pagination.current_page === 1 ? 'disabled' : ''}`}>
      <button className="page-link" onClick={() => setPage(prev => Math.max(prev - 1, 1))}>
        ‹
      </button>
    </li>

    {/* Page numbers */}
    {Array.from({ length: pagination.last_page }, (_, i) => (
      <li
        key={i + 1}
        className={`page-item ${pagination.current_page === i + 1 ? 'active' : ''}`}
      >
        <button className="page-link" onClick={() => setPage(i + 1)}>
          {i + 1}
        </button>
      </li>
    ))}

    {/* Next button */}
    <li className={`page-item ${pagination.current_page === pagination.last_page ? 'disabled' : ''}`}>
      <button className="page-link" onClick={() => setPage(prev => Math.min(prev + 1, pagination.last_page))}>
        ›
      </button>
    </li>
  </ul>
</nav>

                </div>
              </div>
        
              <div className="col-lg-4 mt-4 mt-lg-0 pt-2 pt-lg-0">
                {isLoaded && (
                <div className="map-box rounded15 overflow-hidden" style={{ height: "85%" }}>
                  <ClientMapComponent center={center} markers={markers} />
                </div>
                )}

                <div className="support-card text-white bg-primary rounded15 p-4 mt-4">
                  <div className="p-md-2">
                    <h5 className="font-weight-semibold font-size-18 mb-3">Need Help?</h5>
                    <p className="mb-3">Reach out to our support executive<br />for any help</p>
                    <div className="phone-number my-4">
                      <img src="/assets/images/phone-call-01.svg" className="img-fluid" alt="" />
                      <span>+91-6353385120</span>
                    </div>
                    <a href="https://wa.me/916353385120" target="_blank" className="whatsapp-button">
                      <img src="/assets/images/chat-whatsapp-icon.svg" className="img-fluid" alt="" />
                      Chat on Whatsapp
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* <ClientFooter /> */}
        <div className="mobile-mt-services">
          <ClientFooter />
        </div>

      </main>
    </>
  );

};

export default ClientServices;